import { useSession } from "@pontimed/auth-client";
import { identify, reset } from "@pontimed/analytics/client";
import { useEffect, useRef } from "react";

/**
 * Mirror of the apps/app identity bridge — same effect, same property set,
 * same idempotent behaviour. Admin staff get identified to PostHog so admin
 * actions can be attributed to specific operators (audit trail).
 *
 * Properties: ONLY non-PHI / non-credential JWT claims (role, scope,
 * isSuperadmin, ws). Richer person properties (locale, premium tier, …)
 * are set server-side in `apps/api` via `identifyAuthenticatedUser()`.
 */
export function PostHogIdentityBridge() {
  const { user, status } = useSession();
  const lastIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    const userId = user?.userId ?? null;
    const previousId = lastIdentifiedRef.current;

    if (status === "authenticated" && userId && userId !== previousId) {
      identify(userId, {
        role: user?.role ?? null,
        is_superadmin: user?.isSuperadmin ?? false,
        has_workspace: !!user?.ws,
      });
      lastIdentifiedRef.current = userId;
      return;
    }

    if (status === "unauthenticated" && previousId !== null) {
      reset();
      lastIdentifiedRef.current = null;
    }
  }, [user, status]);

  return null;
}
